/**
 * PopoverExamples.tsx
 * Comprehensive examples of HeroUI Popover component
 * 
 * Showcases all HeroUI Popover features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Popover, PopoverTrigger, PopoverContent } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Input } from '@enfomentis/ui';
import { Switch } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { 
  Info,
  Settings,
  TrendingUp,
  Activity,
  Bell,
  Calendar,
  Clock,
  Target,
  BarChart3,
  PieChart,
  Filter,
  MoreVertical,
  Download,
  Share,
  Edit,
  Trash2,
  Plus,
  Search
} from 'lucide-react';

export const PopoverExamples: React.FC = () => {
  const [showArrow, setShowArrow] = useState(true);
  const [backdrop, setBackdrop] = useState<'transparent' | 'opaque' | 'blur'>('transparent');
  const [isControlledOpen, setIsControlledOpen] = useState(false);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Controls Section */}
        <Card className="bg-surface/50">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Popover Controls</h3>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-6 items-center">
              <div className="flex items-center gap-2">
                <Switch
                  isSelected={showArrow}
                  onValueChange={setShowArrow}
                  size="sm"
                >
                  Show Arrow
                </Switch>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-text-secondary min-w-[70px]">Backdrop:</label>
                <div className="flex gap-1">
                  {(['transparent', 'opaque', 'blur'] as const).map((type) => (
                    <Button
                      key={type}
                      size="sm"
                      variant={backdrop === type ? "solid" : "bordered"}
                      color="primary"
                      onPress={() => setBackdrop(type)}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge color="primary" variant="flat" size="sm">
                  Arrow: {showArrow ? 'On' : 'Off'}
                </Badge>
                <Badge color="secondary" variant="flat" size="sm">
                  Backdrop: {backdrop}
                </Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Popover Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Popover Colors</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Popover color="default" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="default" variant="flat" startContent={<Info className="w-4 h-4" />}>
                  Default
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Default Popover</div>
                  <div className="text-tiny">This is a default styled popover with information content.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover color="primary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="primary" variant="flat" startContent={<Bell className="w-4 h-4" />}>
                  Primary
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Primary Popover</div>
                  <div className="text-tiny">Primary branded popover for important actions or information.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="secondary" variant="flat" startContent={<Activity className="w-4 h-4" />}>
                  Secondary
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Secondary Popover</div>
                  <div className="text-tiny">Secondary popover for supplementary content and actions.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover color="success" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="success" variant="flat" startContent={<TrendingUp className="w-4 h-4" />}>
                  Success
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Success Popover</div>
                  <div className="text-tiny">Positive feedback and successful action confirmations.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover color="warning" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="warning" variant="flat" startContent={<Clock className="w-4 h-4" />}>
                  Warning
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Warning Popover</div>
                  <div className="text-tiny">Important warnings and caution messages for user attention.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover color="danger" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="danger" variant="flat" startContent={<Trash2 className="w-4 h-4" />}>
                  Danger
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Danger Popover</div>
                  <div className="text-tiny">Critical actions and destructive operations confirmation.</div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Popover Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Popover Sizes</h3>
          <div className="flex flex-wrap gap-4">
            <Popover size="sm" color="primary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="sm" variant="bordered">Small Popover</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Small</div>
                  <div className="text-tiny">Compact popover</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover size="md" color="primary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="md" variant="bordered">Medium Popover</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Medium</div>
                  <div className="text-tiny">Standard popover with balanced content space.</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover size="lg" color="primary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="lg" variant="bordered">Large Popover</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-small font-bold">Large</div>
                  <div className="text-tiny">Spacious popover with plenty of room for detailed content and multiple elements.</div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Popover Placements */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Popover Placements</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8">
            
            {/* Top row */}
            <div className="flex justify-center">
              <Popover placement="top-start" color="primary" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Top Start</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Positioned at top-start</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex justify-center">
              <Popover placement="top" color="primary" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Top</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Positioned at top center</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex justify-center">
              <Popover placement="top-end" color="primary" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Top End</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Positioned at top-end</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div></div> {/* Empty space */}
            
            {/* Left column */}
            <Popover placement="left-start" color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="sm" variant="flat">Left Start</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-tiny">Left-start position</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <div></div> {/* Center space */}
            <div></div> {/* Center space */}
            
            {/* Right column */}
            <Popover placement="right-start" color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="sm" variant="flat">Right Start</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-tiny">Right-start position</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover placement="left" color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="sm" variant="flat">Left</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-tiny">Left center position</div>
                </div>
              </PopoverContent>
            </Popover>
            
            <div></div> {/* Center space */}
            <div></div> {/* Center space */}
            
            <Popover placement="right" color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button size="sm" variant="flat">Right</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2">
                  <div className="text-tiny">Right center position</div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Bottom row */}
            <div></div> {/* Empty space */}
            
            <div className="flex justify-center">
              <Popover placement="bottom-start" color="success" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Bottom Start</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Bottom-start position</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex justify-center">
              <Popover placement="bottom" color="success" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Bottom</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Bottom center position</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex justify-center">
              <Popover placement="bottom-end" color="success" showArrow={showArrow} backdrop={backdrop}>
                <PopoverTrigger>
                  <Button size="sm" variant="flat">Bottom End</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-1 py-2">
                    <div className="text-tiny">Bottom-end position</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Advanced Features */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* Controlled popover */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Controlled State</h4>
              </CardHeader>
              <CardBody className="space-y-3">
                <Popover
                  isOpen={isControlledOpen}
                  onOpenChange={setIsControlledOpen}
                  color="primary"
                  showArrow={showArrow}
                  backdrop={backdrop}
                >
                  <PopoverTrigger>
                    <Button variant="bordered" className="w-full">Controlled Target</Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="px-1 py-2">
                      <div className="text-small font-bold">Controlled Popover</div>
                      <div className="text-tiny">This popover's state is controlled programmatically.</div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  onPress={() => setIsControlledOpen(!isControlledOpen)}
                  className="w-full"
                >
                  {isControlledOpen ? 'Close' : 'Open'} Popover
                </Button>
              </CardBody>
            </Card>

            {/* Rich content popover */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Rich Content</h4>
              </CardHeader>
              <CardBody>
                <Popover color="secondary" size="lg" showArrow={showArrow} backdrop={backdrop}>
                  <PopoverTrigger>
                    <Button variant="bordered" startContent={<BarChart3 className="w-4 h-4" />} className="w-full">
                      Trading Dashboard
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="px-1 py-2 min-w-[280px]">
                      <div className="text-small font-bold mb-2">Portfolio Overview</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-tiny">Total Value:</span>
                          <Badge color="success" size="sm">$125,420</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-tiny">Day Change:</span>
                          <Badge color="success" size="sm">+2.3%</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-tiny">Win Rate:</span>
                          <Badge color="primary" size="sm">78%</Badge>
                        </div>
                        <Divider className="my-2" />
                        <div className="flex gap-1">
                          <Button size="sm" variant="flat" color="primary" className="flex-1">View Details</Button>
                          <Button size="sm" variant="flat" color="secondary" className="flex-1">Export</Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </CardBody>
            </Card>

            {/* Interactive popover */}
            <Card>
              <CardHeader>
                <h4 className="text-md font-medium">Interactive Content</h4>
              </CardHeader>
              <CardBody>
                <Popover color="warning" showArrow={showArrow} backdrop={backdrop}>
                  <PopoverTrigger>
                    <Button variant="bordered" startContent={<Settings className="w-4 h-4" />} className="w-full">
                      Quick Settings
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="px-1 py-2 min-w-[240px]">
                      <div className="text-small font-bold mb-3">Quick Settings</div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-tiny">Notifications</span>
                          <Switch size="sm" defaultSelected />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-tiny">Auto-save</span>
                          <Switch size="sm" />
                        </div>
                        <Input
                          size="sm"
                          placeholder="Risk percentage"
                          startContent={<span className="text-tiny">%</span>}
                        />
                        <Button size="sm" color="primary" className="w-full">
                          Save Settings
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Trading Journal Context */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Journal Popovers</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            
            {/* Action menus */}
            <Popover color="default" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="default" variant="flat" startContent={<MoreVertical className="w-4 h-4" />}>
                  Trade Actions
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2 min-w-[160px]">
                  <div className="space-y-1">
                    <Button size="sm" variant="light" startContent={<Edit className="w-3 h-3" />} className="w-full justify-start">
                      Edit Trade
                    </Button>
                    <Button size="sm" variant="light" startContent={<Download className="w-3 h-3" />} className="w-full justify-start">
                      Export
                    </Button>
                    <Button size="sm" variant="light" startContent={<Share className="w-3 h-3" />} className="w-full justify-start">
                      Share
                    </Button>
                    <Divider />
                    <Button size="sm" variant="light" color="danger" startContent={<Trash2 className="w-3 h-3" />} className="w-full justify-start">
                      Delete
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Filter options */}
            <Popover color="primary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="primary" variant="flat" startContent={<Filter className="w-4 h-4" />}>
                  Filters
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2 min-w-[200px]">
                  <div className="text-small font-bold mb-2">Filter Trades</div>
                  <div className="space-y-2">
                    <Input size="sm" placeholder="Symbol" startContent={<Search className="w-3 h-3" />} />
                    <div className="flex gap-1">
                      <Button size="sm" variant="flat" color="success">Wins</Button>
                      <Button size="sm" variant="flat" color="danger">Losses</Button>
                    </div>
                    <div className="flex gap-1 pt-2">
                      <Button size="sm" color="primary" className="flex-1">Apply</Button>
                      <Button size="sm" variant="flat" className="flex-1">Clear</Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Quick stats */}
            <Popover color="success" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="success" variant="flat" startContent={<PieChart className="w-4 h-4" />}>
                  Quick Stats
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2 min-w-[200px]">
                  <div className="text-small font-bold mb-2">This Month</div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-tiny">Total Trades:</span>
                      <span className="text-tiny font-medium">24</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-tiny">Win Rate:</span>
                      <span className="text-tiny font-medium text-success">71%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-tiny">Avg Win:</span>
                      <span className="text-tiny font-medium text-success">$320</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-tiny">Avg Loss:</span>
                      <span className="text-tiny font-medium text-danger">-$180</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-tiny">Profit Factor:</span>
                      <span className="text-tiny font-medium text-primary">2.4</span>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Add new */}
            <Popover color="secondary" showArrow={showArrow} backdrop={backdrop}>
              <PopoverTrigger>
                <Button color="secondary" variant="flat" startContent={<Plus className="w-4 h-4" />}>
                  Add New
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-1 py-2 min-w-[160px]">
                  <div className="text-small font-bold mb-2">Create New</div>
                  <div className="space-y-1">
                    <Button size="sm" variant="light" startContent={<TrendingUp className="w-3 h-3" />} className="w-full justify-start">
                      Trade Entry
                    </Button>
                    <Button size="sm" variant="light" startContent={<Target className="w-3 h-3" />} className="w-full justify-start">
                      Strategy
                    </Button>
                    <Button size="sm" variant="light" startContent={<Calendar className="w-3 h-3" />} className="w-full justify-start">
                      Trading Plan
                    </Button>
                    <Button size="sm" variant="light" startContent={<Bell className="w-3 h-3" />} className="w-full justify-start">
                      Alert
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Usage Guide */}
        <Card className="bg-surface/30">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Usage Guide</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <Chip color="primary" variant="flat" size="sm" className="mb-2">Basic Usage</Chip>
                <pre className="text-xs bg-content1 p-3 rounded-md overflow-x-auto">
{`import { Popover, PopoverTrigger, PopoverContent } from '@enfomentis/ui';

<Popover placement="bottom">
  <PopoverTrigger>
    <Button>Open Popover</Button>
  </PopoverTrigger>
  <PopoverContent>
    <div className="px-1 py-2">
      <div className="text-small font-bold">Title</div>
      <div className="text-tiny">Content goes here</div>
    </div>
  </PopoverContent>
</Popover>`}
                </pre>
              </div>
              
              <div>
                <Chip color="secondary" variant="flat" size="sm" className="mb-2">Key Differences from Tooltip</Chip>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li><strong>Trigger:</strong> Click/manual vs automatic hover (Tooltip)</li>
                  <li><strong>Content:</strong> Rich interactive content vs simple text</li>
                  <li><strong>Purpose:</strong> Actions and complex info vs contextual help</li>
                  <li><strong>Dismissal:</strong> Click outside or ESC vs hover away</li>
                  <li><strong>Accessibility:</strong> Full focus management vs hover-based</li>
                </ul>
              </div>

              <div>
                <Chip color="success" variant="flat" size="sm" className="mb-2">Advanced Features</Chip>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li><strong>Colors:</strong> default, primary, secondary, success, warning, danger</li>
                  <li><strong>Sizes:</strong> sm, md, lg for different content amounts</li>
                  <li><strong>Placements:</strong> 12 positioning options with automatic flipping</li>
                  <li><strong>Backdrops:</strong> transparent, opaque, blur for focus control</li>
                  <li><strong>Interactive Content:</strong> Forms, buttons, switches, and complex layouts</li>
                  <li><strong>Controlled State:</strong> Programmatic control of open/close state</li>
                  <li><strong>Composite Structure:</strong> Popover + PopoverTrigger + PopoverContent</li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </PlaygroundPageLayout>
  );
};